import { useState } from 'react'
import type { Education, EducationInput } from '../../types'

interface EducationFormProps {
  education?: Education
  onSubmit: (data: EducationInput) => Promise<void>
  onCancel: () => void
}

export default function EducationForm({ education, onSubmit, onCancel }: EducationFormProps) {
  const [formData, setFormData] = useState<EducationInput>({
    institution_name: education?.institution_name || '',
    degree: education?.degree || '',
    field_of_study: education?.field_of_study || '',
    start_date: education?.start_date || '',
    end_date: education?.end_date || '',
    gpa: education?.gpa || null,
    description: education?.description || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.institution_name.trim() || !formData.degree.trim()) {
      setError('Institution name and degree are required')
      return
    }

    setSaving(true)
    setError('')
    try {
      await onSubmit(formData)
    } catch {
      setError('Failed to save. Please try again.')
      setSaving(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'gpa' ? (value ? parseFloat(value) : null) : value,
    }))
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-6 space-y-4">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Institution Name *</label>
        <input
          type="text"
          name="institution_name"
          value={formData.institution_name}
          onChange={handleChange}
          placeholder="e.g. Stanford University"
          className="w-full border border-gray-300 rounded-xl py-3 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Degree *</label>
          <input
            type="text"
            name="degree"
            value={formData.degree}
            onChange={handleChange}
            placeholder="e.g. Bachelor of Science"
            className="w-full border border-gray-300 rounded-xl py-3 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Field of Study</label>
          <input
            type="text"
            name="field_of_study"
            value={formData.field_of_study || ''}
            onChange={handleChange}
            placeholder="e.g. Computer Science"
            className="w-full border border-gray-300 rounded-xl py-3 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            name="start_date"
            value={formData.start_date || ''}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-xl py-3 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            name="end_date"
            value={formData.end_date || ''}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-xl py-3 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">GPA</label>
          <input
            type="number"
            name="gpa"
            value={formData.gpa ?? ''}
            onChange={handleChange}
            step="0.01"
            min="0"
            max="4"
            placeholder="e.g. 3.8"
            className="w-full border border-gray-300 rounded-xl py-3 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          value={formData.description || ''}
          onChange={handleChange}
          rows={3}
          placeholder="Notable achievements, activities, coursework..."
          className="w-full border border-gray-300 rounded-xl py-3 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 py-3 px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 py-3 px-4 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50"
        >
          {saving ? 'Saving...' : education ? 'Update' : 'Add Education'}
        </button>
      </div>
    </form>
  )
}
